import type { Distribution, MixedItemTag } from "@memorang/types";
import type { Stat } from "components/RowStats";
import type { TFunction } from "i18next";
import type { EXAM_STATUS } from "types/tag";
import { getExamDaysLeft } from "./stats";

export type ProethicaCompletionStats = {
	completed: number;
	total: number;
	percent: number;
};

type CompletionInput = {
	moduleTags: MixedItemTag[];
	getStatus: (tag: MixedItemTag) => EXAM_STATUS | undefined;
};

export const getProethicaCompletionStats = ({
	moduleTags,
	getStatus,
}: CompletionInput): ProethicaCompletionStats => {
	const total = moduleTags.length;
	const completed = moduleTags.filter(
		(tag) => getStatus(tag) === "COMPLETE",
	).length;
	const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
	return { completed, total, percent };
};

// DonutChart mastery variant fills "advanced" and leaves "none" empty, so a
// completion ring is completed-vs-remaining mapped onto those two buckets.
export const getProethicaCompletionDistribution = ({
	completed,
	total,
}: ProethicaCompletionStats): Distribution => ({
	basic: 0,
	lowIntermediate: 0,
	highIntermediate: 0,
	advanced: completed,
	none: Math.max(0, total - completed),
});

type ExamDateStatOptions = {
	examDate?: string;
	onDateStatItemClick?: () => void;
	t: TFunction;
};

export const getProethicaHeaderStats = (
	{ completed, percent }: ProethicaCompletionStats,
	colors: { purple: string; primary: string },
	examDateOptions?: ExamDateStatOptions,
): Stat[] => {
	const stats: Stat[] = [
		{
			icon: "brain",
			value: `${completed}`,
			label: "Modules completed",
			color: colors.purple,
		},
		{
			icon: "calendar-blank-outline",
			value: `${percent}%`,
			label: "Percent complete",
			color: colors.primary,
			labelColor: colors.primary,
		},
	];

	if (examDateOptions) {
		const { examDate, onDateStatItemClick, t } = examDateOptions;
		const daysLeft = getExamDaysLeft(examDate);
		stats.push({
			icon: "calendar-outline",
			label: examDate
				? t("dashboard.stats.untilExam")
				: t("dashboard.stats.examDate"),
			value: examDate
				? daysLeft && daysLeft > 0
					? t("dashboard.stats.daysLeft", { count: daysLeft })
					: t("dashboard.stats.pastDue")
				: t("dashboard.stats.setExamDate"),
			color: colors.primary,
			labelColor: colors.primary,
			onClick: onDateStatItemClick,
		});
	}

	return stats;
};
